<template>
	<div class="flex-wrapper">
		<Tag :color="statusDisplay" class="status-tag">
			<template #icon>
				<LoadingOutlined v-if="['processing', 'default'].includes(statusDisplay)" />
			</template>
			{{ $t('payment-page.statusValues.' + (payment != null ? payment.status : 'loading')) }}
		</Tag>
		<TypographyTitle :level="2">{{ $t('payment-page.header') }}</TypographyTitle>
	</div>

	<Row>
		<Col span="24" offset="0" :md="{ span: 20 }" class="frame">
			<div class="frame-content">
				<Descriptions :column="1" size="middle" bordered>
					<DescriptionsItem :label="$t('payment-page.status')">
						<Badge class="nobr" :status="statusDisplay" :text="$t('payment-page.statusValues.' + (payment != null ? payment.status : 'created'))" />
					</DescriptionsItem>
					<DescriptionsItem :label="$t('payment-page.description')">
						{{ payment != null ? payment.description : '' }}
					</DescriptionsItem>
					<DescriptionsItem :label="$t('payment-page.period')">
						{{ payment != null ? $t('payment-page.addDaysUnit', payment.add_days) : '' }}
					</DescriptionsItem>
					<DescriptionsItem :label="$t('payment-page.createdAt')">
						<DatetimeFormatter v-if="payment != null" :value="payment.created_at" defaultValue="" />
					</DescriptionsItem>
					<DescriptionsItem :label="$t('payment-page.' + (statusDisplay === 'error' ? 'canceledAt' : 'paidAt'))">
						<DatetimeFormatter v-if="payment != null" :value="payment.paid_at" defaultValue="" />
					</DescriptionsItem>
					<DescriptionsItem :label="$t('payment-page.uuid')">{{ payment != null ? payment.uuid : '' }}</DescriptionsItem>
					<DescriptionsItem v-if="showOwner" :label="$t('payment-page.groupUid')">
						{{ payment != null ? payment.group_uid : '' }}
					</DescriptionsItem>
					<DescriptionsItem v-if="showOwner" :label="$t('payment-page.username')">
						{{ payment != null ? payment.username : '' }}
					</DescriptionsItem>
				</Descriptions>
				<TypographyParagraph v-if="payment != null && payment.status === 'created' && allowMakePayments" class="comment">
					{{ $t('payment-page.comment') }}
					<TypographyLink :href="payment.confirmation_url">
						{{ $t('payment-page.confirmationLink') }}
					</TypographyLink>
				</TypographyParagraph>
			</div>
			<div class="frame-action">
				<div class="total-price-wrapper">
					<span class="total-price-label">{{ $t('payment-page.totalPriceLabel') }}</span>
					<TypographyText strong class="total-price">
						{{ $t('payment-page.totalPriceValue', toRubles(payment != null ? payment.amount : 0)) }}
					</TypographyText>
				</div>
			</div>
		</Col>
	</Row>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { TypographyTitle, TypographyParagraph, TypographyLink, Badge, Row, Col, Tag, Descriptions, DescriptionsItem } from 'ant-design-vue';
import { LoadingOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import { useRoute } from 'vue-router';

import { showErrorMessage } from '@/components/common/errorMessage';
import DatetimeFormatter from '@/components/common/DatetimeFormatter';
import { getPaymentStatusDisplay } from '@/components/payments/getPaymentStatusDisplay';
import { useUserStore } from '@/stores/user';

import { useInterval } from '@/utils/useInterval';


export default {
	components: {
		TypographyTitle, TypographyParagraph, TypographyLink, Badge, Row, Col, Tag, Descriptions, DescriptionsItem,
		DatetimeFormatter,
		LoadingOutlined,
	},
	setup() {
		const route = useRoute();
		const payment = ref(null);
		const updatePaymentData = () => {
			Api.paymentGetOne(route.params.uuid, response => {
				if (response.status === 'ok') {
					payment.value = response.payment;
					if (response.payment.status !== 'created') {
						isRunning.value = false;
					}
				} else {
					showErrorMessage(response.errors);
				}
			});
		}
		const { isRunning } = useInterval(updatePaymentData, 5000, true);
		onMounted(updatePaymentData);
		const statusDisplay = computed(() => getPaymentStatusDisplay(payment.value));
		const userStore = useUserStore();
		return {
			payment,
			statusDisplay,
			showOwner: userStore.allowShowPaymentOwner,
			allowMakePayments: userStore.allowMakePayments,
			toRubles: Utils.toRubles,
		};
	},
}
</script>

<style scoped>
.status-tag {
	font-size: 18px;
	line-height: 29px;
	height: 31px;
	margin-top: 7px;
	margin-right: 12px;
}

.frame {
	max-width: 750px;
	border-radius: 10px;
	border: 1px solid rgba(0, 21, 41, .2);
}

.frame-content,
.frame-action {
	padding: 24px;
}

.comment {
	margin-top: 24px;
}

.frame-action {
	background-color: rgba(0, 0, 0, .02);
	border-top: 1px solid rgba(0, 21, 41, .2);
	display: flex;
	align-items: center;
}

.total-price-wrapper {
	flex-grow: 1;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
}

.total-price-label {
	margin-right: 12px;
	white-space: nowrap;
}

.total-price {
	font-size: 19px;
	white-space: nowrap;
}
</style>
